import React, { useState } from 'react';
import styles from './styles/StockManager.module.css';
import { generateStockRoutinePDF } from '../../services/pdfService';

interface Product {
    idProductos: number;
    NombresProductos: string;
    Precio?: number;
    es_promocion?: boolean | number;
}

interface Local {
    locales_id: number | null;
    nombre_local: string;
}

interface TestResult {
    name: string;
    status: 'pending' | 'running' | 'success' | 'error' | 'warning';
    message: string;
}

interface IntegrationTestsProps {
    products: Product[];
    locales: Local[];
    user: string | null;
    apiUrl: string;
}

const IntegrationTests: React.FC<IntegrationTestsProps> = ({ products, locales, user, apiUrl }) => {
    const [results, setResults] = useState<TestResult[]>([]);
    const [isRunning, setIsRunning] = useState(false);

    const addLog = (message: string, status: 'pending' | 'running' | 'success' | 'error' | 'warning' = 'success', name: string = 'Proceso IT') => {
        setResults(prev => {
            // If name is 'Proceso' and message is the same, just update status? 
            // Better to just append for a full log, or uniquely identify steps.
            // Let's use name for the "Step" and message for the "Detail".
            const index = prev.findIndex(r => r.name === name);
            if (index !== -1 && (status === 'running' || status === 'pending')) {
                const newResults = [...prev];
                newResults[index] = { name, status, message };
                return newResults;
            }
            return [...prev, { name, status, message }];
        });
    };

    const runTests = async () => {
        setIsRunning(true);
        setResults([]);
        addLog('🏁 Iniciando batería de pruebas exhaustiva...', 'running', 'Inicio');

        try {
            // 1. Preparación de Datos
            const scopes: Local[] = [
                ...locales,
                { locales_id: null, nombre_local: 'Global (Todo el sistema)' }
            ];

            const prodsNormal = products.filter(p => !p.es_promocion);
            const prodsPromo = products.filter(p => p.es_promocion === true || p.es_promocion === 1);

            addLog(`Configuración: ${prodsNormal.length} productos, ${prodsPromo.length} promociones, ${scopes.length} ámbitos.`, 'success', 'Preparación');

            // 2. PRUEBAS DE PRODUCTOS INDIVIDUALES (POR LOCAL + GLOBAL)
            for (const scope of scopes) {
                const scopeName = scope.nombre_local;
                const localId = scope.locales_id;

                addLog(`Iniciando pruebas en ámbito: ${scopeName}`, 'running', `Ámbito: ${scopeName}`);

                // Probamos todos los productos en este local discriminadamente
                for (const prod of prodsNormal) {
                    const testQty = 2; // Cantidad pequeña para test
                    const testMotivo = `AUTO_IT_${Date.now()}`;

                    // A. Entrada Individual
                    const resIn = await fetch(`${apiUrl}/admin/stock/movimiento`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            id_producto: prod.idProductos,
                            tipo: 'entrada',
                            cantidad: testQty,
                            motivo: testMotivo,
                            id_local: localId,
                            usuario: user || 'IT-BOT'
                        })
                    });

                    if (!resIn.ok) {
                        addLog(`Error en entrada: ${prod.NombresProductos}`, 'error', `Ámbito: ${scopeName}`);
                        continue;
                    }

                    // B. Verificación de Reporte (Discriminación por local)
                    const reportRes = await fetch(`${apiUrl}/admin/stock/report${localId ? `?id_local=${localId}` : ''}`);
                    const reportData = await reportRes.json();
                    const item = reportData.find((r: any) => r.producto === prod.NombresProductos);

                    if (!item) {
                        addLog(`No se encontró ${prod.NombresProductos} en reporte ${scopeName}`, 'error', `Ámbito: ${scopeName}`);
                    } else if (item.stock_final < testQty) {
                        addLog(`Stock insuficiente en reporte para ${prod.NombresProductos}. Esperado min: ${testQty}, Real: ${item.stock_final}`, 'warning', `Ámbito: ${scopeName}`);
                    }

                    // C. Salida Individual (Limpieza)
                    await fetch(`${apiUrl}/admin/stock/movimiento`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            id_producto: prod.idProductos,
                            tipo: 'salida',
                            cantidad: testQty,
                            motivo: `${testMotivo}_CLEAN`,
                            id_local: localId,
                            usuario: user || 'IT-BOT'
                        })
                    });
                }
                addLog(`✅ Verificados todos los productos (${prodsNormal.length}) en ${scopeName}`, 'success', `Ámbito: ${scopeName}`);
            }

            // 3. PRUEBAS DE PROMOCIONES
            if (prodsPromo.length > 0) {
                addLog('🍔 Iniciando testeo de promociones y combos...', 'running', 'Promociones');

                for (const promo of prodsPromo) {
                    addLog(`Probando Promo: ${promo.NombresProductos}`, 'running', `Promo: ${promo.NombresProductos}`);

                    // Obtener items de la promo
                    const itemsRes = await fetch(`${apiUrl}/admin/promociones/${promo.idProductos}`);
                    const components = await itemsRes.json();

                    if (!components || components.length === 0) {
                        addLog(`Promo ${promo.NombresProductos} sin componentes. Saltando.`, 'warning', `Promo: ${promo.NombresProductos}`);
                        continue;
                    }

                    // Local para el test de promo
                    const testLocal = locales[0]?.locales_id || null;
                    const localLabel = locales[0]?.nombre_local || 'Global';

                    // 1. Capturar stocks previos
                    const beforeReport = await (await fetch(`${apiUrl}/admin/stock/report${testLocal ? `?id_local=${testLocal}` : ''}`)).json();
                    const stocksPre = components.map((c: any) => {
                        const r = beforeReport.find((rep: any) => rep.producto === c.NombresProductos);
                        return { id: c.id_producto, name: c.NombresProductos, stock: r ? r.stock_final : 0, required: c.cantidad };
                    });

                    // 2. Simular VENTA (Salida de Promo)
                    const saleRes = await fetch(`${apiUrl}/orders`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            nombre: 'TEST_IT_PROMO',
                            dni: '0000',
                            montoTotal: promo.Precio || 0,
                            id_local: testLocal,
                            cartItems: [{ ...promo, quantity: 1, es_promocion: true }]
                        })
                    });

                    if (!saleRes.ok) {
                        addLog(`Error al vender promo ${promo.NombresProductos}`, 'error', `Promo: ${promo.NombresProductos}`);
                        continue;
                    }
                    const saleData = await saleRes.json();

                    // 3. Verificar stocks post-venta
                    const afterReport = await (await fetch(`${apiUrl}/admin/stock/report${testLocal ? `?id_local=${testLocal}` : ''}`)).json();
                    let promoOk = true;
                    for (const comp of stocksPre) {
                        const r = afterReport.find((rep: any) => rep.producto === comp.name);
                        const stockActual = r ? r.stock_final : 0;
                        const expected = comp.stock - comp.required;
                        if (stockActual !== expected) {
                            addLog(`Componente ${comp.name} falló. Esperado: ${expected}, Real: ${stockActual}`, 'error', `Promo: ${promo.NombresProductos}`);
                            promoOk = false;
                        }
                    }

                    // 4. Cleanup
                    if (saleData.uuid) {
                        await fetch(`${apiUrl}/admin/orders/${saleData.uuid}`, { method: 'DELETE' });
                    }
                    // Re-sumar componentes
                    for (const comp of components) {
                        await fetch(`${apiUrl}/admin/stock/movimiento`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                id_producto: comp.id_producto,
                                tipo: 'entrada',
                                cantidad: comp.cantidad,
                                motivo: 'IT_PROMO_CLEAN',
                                id_local: testLocal
                            })
                        });
                    }

                    if (promoOk) {
                        addLog(`✅ Promo ${promo.NombresProductos} verificada (Componentes: ${components.length})`, 'success', `Promo: ${promo.NombresProductos}`);
                    }
                }
            }

            // 4. PRUEBAS DE TRANSFERENCIAS MASIVAS (ENTRE TODOS LOS LOCALES)
            const realLocales = locales.filter(l => l.locales_id !== null);
            if (realLocales.length >= 2 && prodsNormal.length > 0) {
                addLog('🚚 Iniciando validación de transferencias entre todos los locales...', 'running', 'Transferencias');

                for (const origin of realLocales) {
                    for (const dest of realLocales) {
                        if (origin.locales_id === dest.locales_id) continue;

                        const p = prodsNormal[0]; // Usamos el primer producto para el test de ruta
                        addLog(`Probando ruta: ${origin.nombre_local} ➔ ${dest.nombre_local}`, 'running', `Transferencia: ${origin.nombre_local} a ${dest.nombre_local}`);

                        const testQty = 5;
                        const resTrans = await fetch(`${apiUrl}/admin/stock/transfer`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                id_producto: p.idProductos,
                                cantidad: testQty,
                                id_local_origen: origin.locales_id,
                                id_local_destino: dest.locales_id,
                                usuario: 'IT-BOT-TRANS'
                            })
                        });

                        if (!resTrans.ok) {
                            addLog(`❌ Error en transferencia de ${origin.nombre_local} a ${dest.nombre_local}`, 'error', `Transferencia: ${origin.nombre_local} a ${dest.nombre_local}`);
                            continue;
                        }

                        // Verificación rápida en reportes
                        const reportOrigin = await (await fetch(`${apiUrl}/admin/stock/report?id_local=${origin.locales_id}`)).json();
                        const reportDest = await (await fetch(`${apiUrl}/admin/stock/report?id_local=${dest.locales_id}`)).json();

                        const itemOrigin = reportOrigin.find((r: any) => r.producto === p.NombresProductos);
                        const itemDest = reportDest.find((r: any) => r.producto === p.NombresProductos);

                        if (!itemOrigin || !itemDest) {
                            addLog(`⚠️ Dificultad para verificar stock después de transferencia en ${origin.nombre_local} o ${dest.nombre_local}`, 'warning', `Transferencia: ${origin.nombre_local} a ${dest.nombre_local}`);
                        }

                        // Cleanup: Devolver stock
                        await fetch(`${apiUrl}/admin/stock/transfer`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                id_producto: p.idProductos,
                                cantidad: testQty,
                                id_local_origen: dest.locales_id,
                                id_local_destino: origin.locales_id,
                                usuario: 'IT-BOT-CLEAN'
                            })
                        });

                        addLog(`✅ Ruta verificada: ${origin.nombre_local} a ${dest.nombre_local}`, 'success', `Transferencia: ${origin.nombre_local} a ${dest.nombre_local}`);
                    }
                }
            } else if (realLocales.length < 2) {
                addLog('ℹ️ No hay suficientes locales reales para probar transferencias.', 'warning', 'Transferencias');
            }

            addLog('✨ Batería de pruebas finalizada exitosamente.', 'success', 'Fin');

        } catch (error: any) {
            addLog(`Error fatal: ${error.message}`, 'error', 'Error Crítico');
        } finally {
            setIsRunning(false);
        }
    };

    const handleResetStockInicial = async () => {
        if (!window.confirm("¿Está seguro de llevar a 0 el stock inicial de todos los productos? Esta acción registrará movimientos de ajuste con fecha de ayer para compensar las diferencias.")) return;

        setIsRunning(true);
        addLog('🔄 Iniciando reajuste masivo de stock inicial...', 'running', 'Reajuste');

        try {
            const res = await fetch(`${apiUrl}/admin/stock/reset-initial`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            const data = await res.json();
            if (res.ok) {
                addLog(`✅ ${data.message}`, 'success', 'Reajuste');
            } else {
                addLog(`❌ Error: ${data.error}`, 'error', 'Reajuste');
            }
        } catch (error: any) {
            addLog(`❌ Error fatal: ${error.message}`, 'error', 'Reajuste');
        } finally {
            setIsRunning(false);
        }
    };

    const handleGenerateStockRoutine = (type: 'MAÑANA' | 'NOCHE') => {
        // Filtrar solo productos normales para el stock rotativo (no promos)
        const normalProducts = products.filter(p => !p.es_promocion);
        if (normalProducts.length === 0) {
            addLog('⚠️ No hay productos para generar la planilla.', 'warning', 'PDF');
            return;
        }
        generateStockRoutinePDF(normalProducts, type);
        addLog(`📄 Planilla de Stock Rotativo (${type}) generada.`, 'success', 'PDF');
    };

    return (
        <div style={{ padding: '20px', backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', borderBottom: '2px solid #eee', paddingBottom: '15px' }}>
                <div>
                    <h3 style={{ margin: 0, color: '#48111d' }}>Arquitectura de Pruebas de Integración</h3>
                    <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '0.9rem' }}>Validación masiva de stock, locales y promociones.</p>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button
                        onClick={() => handleGenerateStockRoutine('MAÑANA')}
                        disabled={isRunning}
                        style={{
                            padding: '12px 20px',
                            backgroundColor: '#2980b9',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: isRunning ? 'not-allowed' : 'pointer',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        ☀️ Planilla Mañana
                    </button>
                    <button
                        onClick={() => handleGenerateStockRoutine('NOCHE')}
                        disabled={isRunning}
                        style={{
                            padding: '12px 20px',
                            backgroundColor: '#2c3e50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: isRunning ? 'not-allowed' : 'pointer',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        🌙 Planilla Noche
                    </button>
                    <button
                        onClick={handleResetStockInicial}
                        disabled={isRunning}
                        style={{
                            padding: '12px 20px',
                            backgroundColor: isRunning ? '#ccc' : '#ef6c00',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: isRunning ? 'not-allowed' : 'pointer',
                            fontWeight: 'bold'
                        }}
                    >
                        {isRunning ? 'Procesando...' : '🔄 Reajustar Stock'}
                    </button>
                    <button
                        onClick={runTests}
                        disabled={isRunning}
                        style={{
                            padding: '12px 20px',
                            backgroundColor: isRunning ? '#ccc' : '#48111d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: isRunning ? 'not-allowed' : 'pointer',
                            fontWeight: 'bold'
                        }}
                    >
                        {isRunning ? 'Efectuando Pruebas...' : 'Iniciar Testeo'}
                    </button>
                </div>
            </div>

            <div style={{ maxHeight: '600px', overflowY: 'auto', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                {results.length === 0 && !isRunning && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                        <p>Listo para comenzar la validación del sistema.</p>
                        <small>Se probarán todos los productos en todos los locales de forma independiente.</small>
                    </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {results.map((res, i) => (
                        <div key={i} style={{
                            padding: '12px 15px',
                            borderRadius: '8px',
                            backgroundColor: 'white',
                            border: '1px solid #eee',
                            borderLeft: `6px solid ${res.status === 'success' ? '#4caf50' :
                                res.status === 'error' ? '#f44336' :
                                    res.status === 'warning' ? '#ff9800' :
                                        res.status === 'running' ? '#2196f3' : '#ccc'
                                }`,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#444', textTransform: 'uppercase' }}>{res.name}</span>
                                <span style={{
                                    fontSize: '0.7rem',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    backgroundColor:
                                        res.status === 'success' ? '#e8f5e9' :
                                            res.status === 'error' ? '#ffebee' :
                                                res.status === 'warning' ? '#fff3e0' :
                                                    res.status === 'running' ? '#e3f2fd' : '#f5f5f5',
                                    color:
                                        res.status === 'success' ? '#2e7d32' :
                                            res.status === 'error' ? '#c62828' :
                                                res.status === 'warning' ? '#ef6c00' :
                                                    res.status === 'running' ? '#1565c0' : '#757575'
                                }}>
                                    {res.status.toUpperCase()}
                                </span>
                            </div>
                            <span style={{ fontSize: '0.95rem', color: '#111' }}>{res.message}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default IntegrationTests;
